import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ProductDetailAIChatModalProps {
  open: boolean;
  onClose: () => void;
  productPrice?: number;
}

export const ProductDetailAIChatModal = ({
  open,
  onClose,
  productPrice = 500000,
}: ProductDetailAIChatModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: `Hello! I'm your AI loan advisor. I can help you analyze loan programs, compare rates, and provide personalized recommendations for your $${productPrice.toLocaleString(
        'en-US',
        { maximumFractionDigits: 0 }
      )} property. What would you like to know?`,
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');

  const handleSendMessage = () => {
    if (!inputText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');

    setTimeout(() => {
      const aiResponse = generateAIResponse(inputText);
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    }, 1000);
  };

  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();

    if (
      input.includes('conventional') ||
      input.includes('fha') ||
      input.includes('va')
    ) {
      return `Based on your property value of $${productPrice.toLocaleString(
        'en-US',
        { maximumFractionDigits: 0 }
      )}, here are my recommendations:

• **Conventional Loan**: Best for borrowers with 20%+ down payment, lower PMI costs
• **FHA Loan**: Great for first-time buyers, lower credit score requirements (3.5% down)
• **VA Loan**: Excellent for veterans with 0% down payment and competitive rates

Would you like me to analyze specific loan programs for your situation?`;
    }

    if (input.includes('rate') || input.includes('interest')) {
      return `Current market rates are trending around 6.5-7.2% for conventional loans. However, rates vary based on:

• Credit score (aim for 740+ for best rates)
• Down payment amount
• Loan term (15 vs 30 years)
• Property type and location

I can help you calculate specific rates based on your profile. What's your credit score range?`;
    }

    if (input.includes('down payment') || input.includes('downpayment')) {
      return `For a $${productPrice.toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })} property, here are your down payment options:

• **3.5% down**: $${(productPrice * 0.035).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })} (FHA)
• **5% down**: $${(productPrice * 0.05).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })} (Conventional)
• **10% down**: $${(productPrice * 0.1).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })} (Conventional)
• **20% down**: $${(productPrice * 0.2).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })} (Conventional, no PMI)

What's your target down payment amount?`;
    }

    if (input.includes('monthly') || input.includes('payment')) {
      const monthlyPayment = (productPrice * 0.07) / 12;
      return `For a $${productPrice.toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })} property with 20% down at 7% interest:

**Estimated Monthly Payment: $${monthlyPayment.toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })}**

This includes:
• Principal & Interest: ~$${(monthlyPayment * 0.8).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })}
• Property Tax: ~$${(monthlyPayment * 0.15).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })}
• Insurance: ~$${(monthlyPayment * 0.05).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      })}

Would you like me to calculate payments for different scenarios?`;
    }

    return `I understand you're asking about "${userInput}". I can help you with:

• Loan program comparisons
• Rate analysis and trends
• Down payment strategies
• Monthly payment calculations
• Credit score optimization
• Property investment analysis

What specific aspect would you like to explore further?`;
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        className: 'taste-rise taste-panel',
        sx: {
          background: '#ffffff',
          borderRadius: isMobile ? 0 : 3,
          height: isMobile ? '100vh' : '80vh',
          maxHeight: isMobile ? '100vh' : '80vh',
          boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.12)}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          background: '#ffffff',
          px: isMobile ? 2 : 3,
          py: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <SmartToyIcon sx={{ color: theme.palette.secondary.main }} />
          <Box>
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              fontWeight={700}
              className="font-display"
              sx={{
                color: theme.palette.text.primary,
                letterSpacing: '-0.02em',
              }}
            >
              AI Insights
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mt: 0.25 }}
            >
              Get AI-powered loan recommendations
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label="$50"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              color: theme.palette.text.primary,
              fontWeight: 600,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            }}
          />
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: theme.palette.text.primary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          background: '#ffffff',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: isMobile ? 1.5 : 2,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            background: theme.palette.background.default,
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                mb: 1,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  maxWidth: isMobile ? '85%' : '70%',
                  p: isMobile ? 1.5 : 2,
                  background: message.isUser
                    ? alpha(theme.palette.primary.main, 0.08)
                    : '#ffffff',
                  border: `1px solid ${
                    message.isUser
                      ? alpha(theme.palette.primary.main, 0.15)
                      : alpha(theme.palette.text.primary, 0.08)
                  }`,
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  {message.isUser ? (
                    <PersonIcon
                      sx={{
                        fontSize: isMobile ? 14 : 16,
                        color: theme.palette.primary.main,
                      }}
                    />
                  ) : (
                    <SmartToyIcon
                      sx={{
                        fontSize: isMobile ? 14 : 16,
                        color: theme.palette.secondary.main,
                      }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {message.isUser ? 'You' : 'AI Advisor'}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-line',
                    lineHeight: 1.6,
                    fontSize: isMobile ? '0.875rem' : '0.95rem',
                    color: theme.palette.text.primary,
                  }}
                >
                  {message.text}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 1,
                    fontSize: '0.75rem',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            p: isMobile ? 1.5 : 2,
            borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
            display: 'flex',
            gap: 1,
            background: '#ffffff',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={isMobile ? 2 : 3}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me about loan programs, rates, payments, or anything else..."
            sx={{
              '& .MuiOutlinedInput-root': {
                color: theme.palette.text.primary,
                background: '#ffffff',
                '& fieldset': {
                  borderColor: alpha(theme.palette.text.primary, 0.23),
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.text.primary, 0.4),
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.secondary.main,
                },
              },
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '0.875rem' : '1rem',
                padding: isMobile ? 1.5 : 2,
              },
              '& .MuiInputBase-input::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 1,
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputText.trim()}
            sx={{
              bgcolor: theme.palette.secondary.main,
              color: '#ffffff',
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
              '&.Mui-disabled': {
                bgcolor: alpha(theme.palette.text.primary, 0.12),
                color: alpha(theme.palette.text.primary, 0.3),
              },
            }}
          >
            <SendIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
          </IconButton>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 2 : 3,
          borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          background: '#ffffff',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1.5 : 1,
        }}
      >
        <Button
          onClick={onClose}
          fullWidth={isMobile}
          sx={{ color: theme.palette.text.secondary }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          fullWidth={isMobile}
          sx={{
            fontWeight: 700,
            backgroundColor: theme.palette.secondary.main,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Purchase Access ($50)
        </Button>
      </DialogActions>
    </Dialog>
  );
};
